import { redrawSkyViewWithOptions } from '../../../../../../utils/three/snapshot';
import { SkyView } from '../../../../../../viewer/skyview';

/** The largest side of the screenshot, in pixels. */
const MAX_SIDE_PX = 4096;

/**
 * The part of an image of this size, in its middle, with the aspect ratio (width / height), at
 * most 4096 px a side: where to cut it from, and the size to draw it in.
 */
export function getScreenshotCrop(width: number, height: number, aspect: number) {
  const cropWidth = Math.min(width, height * aspect);
  const cropHeight = cropWidth / aspect;
  const factor = Math.min(1, MAX_SIDE_PX / cropWidth, MAX_SIDE_PX / cropHeight);

  return {
    x: (width - cropWidth) / 2,
    y: (height - cropHeight) / 2,
    width: cropWidth,
    height: cropHeight,
    outputWidth: Math.max(1, Math.round(cropWidth * factor)),
    outputHeight: Math.max(1, Math.round(cropHeight * factor)),
  };
}

const loadImage = (url: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('The screenshot could not be read.'));
    image.src = url;
  });

/**
 * Takes a picture of the view, as the react-pdf report does, and crops it to the aspect ratio of
 * the page's map area.
 *
 * @param aspect The map area's width / height.
 * @returns A PNG.
 */
async function captureScreenshot(
  skyview: SkyView,
  aspect: number,
  whiteBackground: boolean,
): Promise<Uint8Array> {
  redrawSkyViewWithOptions(skyview, { clearColor: whiteBackground ? 0xffffff : undefined });

  const getSkyviewScreenshot = (window as { getSkyviewScreenshot?: () => Promise<string> })
    .getSkyviewScreenshot;
  if (typeof getSkyviewScreenshot !== 'function') {
    throw new Error('The view can not be captured.');
  }

  const image = await loadImage(await getSkyviewScreenshot());
  const crop = getScreenshotCrop(image.width, image.height, aspect);

  const canvas = document.createElement('canvas');
  canvas.width = crop.outputWidth;
  canvas.height = crop.outputHeight;
  canvas
    .getContext('2d')
    ?.drawImage(
      image,
      crop.x,
      crop.y,
      crop.width,
      crop.height,
      0,
      0,
      crop.outputWidth,
      crop.outputHeight,
    );

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) {
    throw new Error('The screenshot could not be made.');
  }

  return new Uint8Array(await blob.arrayBuffer());
}

/**
 * Exported as a collection to allow for mocking, as tests can't draw.
 */
export const screenshotHelpers = { captureScreenshot };
